/*
 * File: DriveHistoryItem.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type { SerializedEditorState } from 'lexical';

import { RichTextViewer } from 'components/Editor';

import { getAttendanceLabel } from '../../utils';
import DriveNotes from './DriveNotes';

/**
 * Props for the DriveHistoryItem component.
 */
interface DriveHistoryItemProps {
  /**
   * Display label for the drive number (e.g., "#1", "#2", "Final").
   */
  driveLabel: string;

  /**
   * Formatted date of the drive session.
   * @example "Jan 15, 2025"
   */
  date: string;

  /**
   * Formatted start time of the drive.
   * @example "2:00 PM"
   */
  startTime: string;

  /**
   * Formatted end time of the drive. If omitted, only start time is shown.
   * @example "3:00 PM"
   */
  endTime?: string;

  /**
   * List of instructor names who supervised this drive session.
   */
  instructors: string[];

  /**
   * Attendance status of the student for this drive.
   * - `'present'` → Student attended
   * - `'absent'` → Student was marked absent
   * - `'no_permit'` → Student attended but lacked required permit
   * - `null`/`undefined` → Attendance not yet recorded
   */
  attendance?: 'present' | 'absent' | 'no_permit' | null;

  /**
   * Whether this drive session was canceled.
   * Takes precedence over attendance when determining visual status.
   */
  isCanceled?: boolean;

  /**
   * Optional rich-text notes from the instructor, stored as a Lexical editor state.
   * Will be rendered using RichTextViewer if provided.
   */
  notes?: SerializedEditorState | null;
}

/**
 * Visual theme configuration for different drive statuses.
 * Ensures consistent coloring and icons across the Chip and left border.
 */
type DriveStatusTheme = {
  /** Text shown inside the status Chip (null = no chip) */
  label: string | null;
  /** MUI Chip color variant */
  chipColor: 'default' | 'success' | 'error';
  /** Color token used for the thick left border of the card */
  borderColor: string;
  /** Icon displayed inside the Chip */
  icon: JSX.Element | undefined;
};

/**
 * A card component displaying a single historical driving session.
 *
 * Features:
 * - Prominent drive number and status chip
 * - Date and time display
 * - Colored left border matching the status chip
 * - Instructor names with icon
 * - Optional rich-text notes section
 *
 * The visual status (chip color, border color, icon, label) is determined by:
 * 1. `isCanceled` → "Canceled" (grey)
 * 2. `attendance === 'present'` → Success (green)
 * 3. `attendance === 'absent'` or `'no_permit'` → Error (red)
 * 4. No status → Neutral (divider color, no chip)
 *
 * @param props - {@link DriveHistoryItemProps}
 * @returns A styled Card representing one drive history entry
 *
 * @example
 * ```tsx
 * <DriveHistoryItem
 *   driveLabel="#3"
 *   date="Mar 5, 2025"
 *   startTime="4:00 PM"
 *   endTime="5:30 PM"
 *   instructors={["Sarah Miller"]}
 *   attendance="present"
 *   notes={editorState}
 * />
 * ```
 */
export default function DriveHistoryItem({
  driveLabel,
  date,
  startTime,
  endTime,
  instructors,
  attendance,
  isCanceled = false,
  notes,
}: DriveHistoryItemProps) {
  /**
   * Determines the complete visual theme (chip, border, icon, label) based on cancellation and attendance.
   * This is the single source of truth — guarantees chip and border always match.
   */
  const getStatusTheme = (): DriveStatusTheme => {
    if (isCanceled) {
      return {
        label: 'Canceled',
        chipColor: 'default',
        borderColor: 'grey.500',
        icon: <CancelIcon />,
      };
    }

    if (attendance === 'present') {
      return {
        label: getAttendanceLabel('present'),
        chipColor: 'success',
        borderColor: 'success.main',
        icon: <CheckCircleIcon />,
      };
    }

    if (attendance === 'absent') {
      return {
        label: getAttendanceLabel('absent'),
        chipColor: 'error',
        borderColor: 'error.main',
        icon: <ErrorIcon />,
      };
    }

    if (attendance === 'no_permit') {
      return {
        label: getAttendanceLabel('no_permit'),
        chipColor: 'error',
        borderColor: 'error.main',
        icon: <BlockIcon />,
      };
    }

    // No status recorded
    return {
      label: null,
      chipColor: 'default',
      borderColor: 'divider',
      icon: undefined,
    };
  };

  const status = getStatusTheme();
  const timeDisplay = endTime ? `${startTime} — ${endTime}` : startTime;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderWidth: 2,
        borderLeft: 8,
        borderLeftColor: status.borderColor,
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.25 } }}>
        {/* Header Section */}
        <Grid container justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 600 }}>
                Drive {driveLabel}
              </Typography>

              {status.label && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={status.chipColor}
                  icon={status.icon}
                  label={status.label}
                  sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                />
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="body2" fontWeight={500}>
                {date}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {timeDisplay}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Instructor */}
        {instructors.length > 0 && (
          <Box mt={1.5} display="flex" alignItems="center" gap={0.75} color="text.secondary">
            <PersonIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">
              {instructors.join(', ')}
            </Typography>
          </Box>
        )}

        {/* Notes */}
        {notes && (
          <Box mt={2}>
            <DriveNotes>
              <RichTextViewer value={notes} />
            </DriveNotes>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
